// ------------------------------------------------------------
// @project Pagali - Plataforma de Pagamentos
// @file banking.service.ts
// @author Arlinda Peixoto
// @company Pagali
// @description Serviço de APIs bancárias
// @date 2026-01-28
// ------------------------------------------------------------

import api from './api';
import type {
  Bank,
  BankAccount,
  BankBalance,
  BankTransaction,
  AggregatedBalance,
  Transfer,
  TransferP2PRequest,
  TransferBankRequest,
  TransferInternationalRequest,
  TransferCorridor,
  ExchangeQuote,
  TransferLimits,
  ScheduledTransfer,
  InstantKey,
  InstantKeyLookup,
  PaymentQR,
  InstantPaymentRequest,
  QRPaymentRequest,
  CreditProduct,
  CreditSimulation,
  PreApprovedCredit,
  CreditApplication,
  CreditContract,
  SpendingAnalysis,
  Budget,
  SavingsGoal,
  FinancialHealthScore,
  KycStatus,
  AccountLimits,
  SecurityAlert,
  ServiceProvider,
  BillDetails,
  BillPaymentRequest,
  BillPaymentResult,
  BillFavorite,
  RecurringBill,
  BillAnalytics,
  BankingFilters,
  InstantKeyType,
} from '../types';

const BANKING_BASE = '/client/banking';

class BankingService {
  // ============================================
  // OPEN BANKING - BANCOS E CONTAS
  // ============================================

  async getBanks(): Promise<Bank[]> {
    const response = await api.get<{
      success: boolean;
      data?: Bank[];
      error?: string;
    }>(`${BANKING_BASE}/banks`);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Falha ao carregar bancos');
  }

  async getAccounts(): Promise<BankAccount[]> {
    const response = await api.get<{
      success: boolean;
      data?: BankAccount[];
      error?: string;
    }>(`${BANKING_BASE}/accounts`);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Falha ao carregar contas');
  }

  async connectAccount(bankCode: string, credentials: Record<string, string>): Promise<BankAccount> {
    const response = await api.post<{
      success: boolean;
      data?: BankAccount;
      error?: string;
    }>(`${BANKING_BASE}/accounts/connect`, { bankCode, credentials });

    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Falha ao conectar conta');
  }

  async disconnectAccount(accountId: string): Promise<void> {
    const response = await api.delete<{
      success: boolean;
      error?: string;
    }>(`${BANKING_BASE}/accounts/${accountId}`);

    if (!response.data.success) {
      throw new Error(response.data.error || 'Falha ao desconectar conta');
    }
  }

  async getAccountBalance(accountId: string): Promise<BankBalance> {
    const response = await api.get<{
      success: boolean;
      data?: BankBalance;
      error?: string;
    }>(`${BANKING_BASE}/accounts/${accountId}/balance`);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Falha ao carregar saldo');
  }

  async getAccountStatement(accountId: string, filters?: BankingFilters): Promise<BankTransaction[]> {
    const params = new URLSearchParams();
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);

    const query = params.toString();
    const url = `${BANKING_BASE}/accounts/${accountId}/statement${query ? `?${query}` : ''}`;

    const response = await api.get<{
      success: boolean;
      data?: BankTransaction[];
      error?: string;
    }>(url);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Falha ao carregar extrato');
  }

  async getAggregatedBalance(): Promise<AggregatedBalance> {
    const response = await api.get<{
      success: boolean;
      data?: AggregatedBalance;
      error?: string;
    }>(`${BANKING_BASE}/aggregate`);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Falha ao carregar saldo agregado');
  }

  // ============================================
  // TRANSFERÊNCIAS
  // ============================================

  async transferP2P(request: TransferP2PRequest): Promise<Transfer> {
    const response = await api.post<{
      success: boolean;
      data?: Transfer;
      error?: string;
    }>(`${BANKING_BASE}/transfers/p2p`, request);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Falha na transferência P2P');
  }

  async transferBank(request: TransferBankRequest): Promise<Transfer> {
    const response = await api.post<{
      success: boolean;
      data?: Transfer;
      error?: string;
    }>(`${BANKING_BASE}/transfers/bank`, request);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Falha na transferência bancária');
  }

  async transferInternational(request: TransferInternationalRequest): Promise<Transfer> {
    const response = await api.post<{
      success: boolean;
      data?: Transfer;
      error?: string;
    }>(`${BANKING_BASE}/transfers/international`, request);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Falha na transferência internacional');
  }

  async getTransferHistory(filters?: BankingFilters): Promise<Transfer[]> {
    const params = new URLSearchParams();
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);
    if (filters?.type) params.append('type', filters.type);
    if (filters?.status) params.append('status', filters.status);

    const query = params.toString();
    const url = `${BANKING_BASE}/transfers${query ? `?${query}` : ''}`;

    const response = await api.get<{
      success: boolean;
      data?: Transfer[];
      error?: string;
    }>(url);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Falha ao carregar histórico');
  }

  async getTransferLimits(): Promise<TransferLimits> {
    const response = await api.get<{
      success: boolean;
      data?: TransferLimits;
      error?: string;
    }>(`${BANKING_BASE}/transfers/limits`);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Falha ao carregar limites');
  }

  async getCorridors(): Promise<TransferCorridor[]> {
    const response = await api.get<{
      success: boolean;
      data?: TransferCorridor[];
      error?: string;
    }>(`${BANKING_BASE}/transfers/corridors`);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Falha ao carregar corredores');
  }

  async getExchangeQuote(corridorId: string, amount: number): Promise<ExchangeQuote> {
    const response = await api.post<{
      success: boolean;
      data?: ExchangeQuote;
      error?: string;
    }>(`${BANKING_BASE}/transfers/quote`, { corridorId, amount });

    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Falha ao obter cotação');
  }

  async scheduleTransfer(transferData: TransferBankRequest, scheduledDate: string, frequency?: string): Promise<ScheduledTransfer> {
    const response = await api.post<{
      success: boolean;
      data?: ScheduledTransfer;
      error?: string;
    }>(`${BANKING_BASE}/transfers/schedule`, {
      ...transferData,
      scheduledDate,
      frequency,
    });

    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Falha ao agendar transferência');
  }

  // ============================================
  // PAGAMENTOS INSTANTÂNEOS
  // ============================================

  async getInstantKeys(): Promise<InstantKey[]> {
    const response = await api.get<{
      success: boolean;
      data?: InstantKey[];
      error?: string;
    }>(`${BANKING_BASE}/instant/keys`);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Falha ao carregar chaves');
  }

  async registerInstantKey(type: InstantKeyType, value: string): Promise<InstantKey> {
    const response = await api.post<{
      success: boolean;
      data?: InstantKey;
      error?: string;
    }>(`${BANKING_BASE}/instant/keys`, { type, value });

    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Falha ao registrar chave');
  }

  async deleteInstantKey(keyId: string): Promise<void> {
    const response = await api.delete<{
      success: boolean;
      error?: string;
    }>(`${BANKING_BASE}/instant/keys/${keyId}`);

    if (!response.data.success) {
      throw new Error(response.data.error || 'Falha ao remover chave');
    }
  }

  async lookupInstantKey(keyType: InstantKeyType, keyValue: string): Promise<InstantKeyLookup> {
    const response = await api.post<{
      success: boolean;
      data?: InstantKeyLookup;
      error?: string;
    }>(`${BANKING_BASE}/instant/keys/lookup`, { keyType, keyValue });

    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Falha ao buscar chave');
  }

  async instantPay(request: InstantPaymentRequest): Promise<Transfer> {
    const response = await api.post<{
      success: boolean;
      data?: Transfer;
      error?: string;
    }>(`${BANKING_BASE}/instant/pay`, request);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Falha no pagamento instantâneo');
  }

  async generateQR(amount?: number, description?: string): Promise<PaymentQR> {
    const response = await api.post<{
      success: boolean;
      data?: PaymentQR;
      error?: string;
    }>(`${BANKING_BASE}/instant/qr/generate`, { amount, description });

    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Falha ao gerar QR Code');
  }

  async payWithQR(request: QRPaymentRequest): Promise<Transfer> {
    const response = await api.post<{
      success: boolean;
      data?: Transfer;
      error?: string;
    }>(`${BANKING_BASE}/instant/qr/pay`, request);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Falha no pagamento QR');
  }

  async getMyQRCodes(): Promise<PaymentQR[]> {
    const response = await api.get<{
      success: boolean;
      data?: PaymentQR[];
      error?: string;
    }>(`${BANKING_BASE}/instant/qr`);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Falha ao carregar QR Codes');
  }

  // ============================================
  // CRÉDITO
  // ============================================

  async getCreditProducts(): Promise<CreditProduct[]> {
    const response = await api.get<{
      success: boolean;
      data?: CreditProduct[];
      error?: string;
    }>(`${BANKING_BASE}/credit/products`);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Falha ao carregar produtos');
  }

  async simulateCredit(productId: string, amount: number, term: number): Promise<CreditSimulation> {
    const response = await api.post<{
      success: boolean;
      data?: CreditSimulation;
      error?: string;
    }>(`${BANKING_BASE}/credit/simulate`, { productId, amount, term });

    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Falha na simulação');
  }

  async getPreApprovedCredits(): Promise<PreApprovedCredit[]> {
    const response = await api.get<{
      success: boolean;
      data?: PreApprovedCredit[];
      error?: string;
    }>(`${BANKING_BASE}/credit/pre-approved`);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Falha ao carregar pré-aprovados');
  }

  async applyForCredit(productId: string, amount: number, term: number): Promise<CreditApplication> {
    const response = await api.post<{
      success: boolean;
      data?: CreditApplication;
      error?: string;
    }>(`${BANKING_BASE}/credit/apply`, { productId, amount, term });

    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Falha ao solicitar crédito');
  }

  async getCreditApplications(): Promise<CreditApplication[]> {
    const response = await api.get<{
      success: boolean;
      data?: CreditApplication[];
      error?: string;
    }>(`${BANKING_BASE}/credit/applications`);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Falha ao carregar pedidos');
  }

  async getCreditContracts(): Promise<CreditContract[]> {
    const response = await api.get<{
      success: boolean;
      data?: CreditContract[];
      error?: string;
    }>(`${BANKING_BASE}/credit/contracts`);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Falha ao carregar contratos');
  }

  async getCreditContractDetails(contractId: string): Promise<CreditContract> {
    const response = await api.get<{
      success: boolean;
      data?: CreditContract;
      error?: string;
    }>(`${BANKING_BASE}/credit/contracts/${contractId}`);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Falha ao carregar contrato');
  }

  // ============================================
  // PFM - GESTÃO FINANCEIRA PESSOAL
  // ============================================

  async getSpendingAnalysis(period?: string): Promise<SpendingAnalysis> {
    const url = period
      ? `${BANKING_BASE}/pfm/spending?period=${period}`
      : `${BANKING_BASE}/pfm/spending`;

    const response = await api.get<{
      success: boolean;
      data?: SpendingAnalysis;
      error?: string;
    }>(url);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Falha ao carregar análise');
  }

  async getBudgets(): Promise<Budget[]> {
    const response = await api.get<{
      success: boolean;
      data?: Budget[];
      error?: string;
    }>(`${BANKING_BASE}/pfm/budgets`);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Falha ao carregar orçamentos');
  }

  async createBudget(categoryId: string, amount: number, period: 'weekly' | 'monthly' | 'yearly'): Promise<Budget> {
    const response = await api.post<{
      success: boolean;
      data?: Budget;
      error?: string;
    }>(`${BANKING_BASE}/pfm/budgets`, { categoryId, amount, period });

    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Falha ao criar orçamento');
  }

  async updateBudget(budgetId: string, amount: number): Promise<Budget> {
    const response = await api.put<{
      success: boolean;
      data?: Budget;
      error?: string;
    }>(`${BANKING_BASE}/pfm/budgets/${budgetId}`, { amount });

    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Falha ao atualizar orçamento');
  }

  async deleteBudget(budgetId: string): Promise<void> {
    const response = await api.delete<{
      success: boolean;
      error?: string;
    }>(`${BANKING_BASE}/pfm/budgets/${budgetId}`);

    if (!response.data.success) {
      throw new Error(response.data.error || 'Falha ao remover orçamento');
    }
  }

  async getSavingsGoals(): Promise<SavingsGoal[]> {
    const response = await api.get<{
      success: boolean;
      data?: SavingsGoal[];
      error?: string;
    }>(`${BANKING_BASE}/pfm/goals`);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Falha ao carregar metas');
  }

  async createSavingsGoal(name: string, targetAmount: number, deadline?: string): Promise<SavingsGoal> {
    const response = await api.post<{
      success: boolean;
      data?: SavingsGoal;
      error?: string;
    }>(`${BANKING_BASE}/pfm/goals`, { name, targetAmount, deadline });

    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Falha ao criar meta');
  }

  async contributeToGoal(goalId: string, amount: number): Promise<SavingsGoal> {
    const response = await api.post<{
      success: boolean;
      data?: SavingsGoal;
      error?: string;
    }>(`${BANKING_BASE}/pfm/goals/${goalId}/contribute`, { amount });

    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Falha ao contribuir');
  }

  async getFinancialHealthScore(): Promise<FinancialHealthScore> {
    const response = await api.get<{
      success: boolean;
      data?: FinancialHealthScore;
      error?: string;
    }>(`${BANKING_BASE}/pfm/health-score`);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Falha ao carregar score');
  }

  // ============================================
  // KYC E SEGURANÇA
  // ============================================

  async getKycStatus(): Promise<KycStatus> {
    const response = await api.get<{
      success: boolean;
      data?: KycStatus;
      error?: string;
    }>(`${BANKING_BASE}/kyc/status`);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Falha ao carregar status KYC');
  }

  async startKycVerification(level: string): Promise<{ uploadUrl: string; sessionId: string }> {
    const response = await api.post<{
      success: boolean;
      data?: { uploadUrl: string; sessionId: string };
      error?: string;
    }>(`${BANKING_BASE}/kyc/start`, { level });

    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Falha ao iniciar verificação');
  }

  async uploadKycDocument(documentType: string, file: File): Promise<void> {
    const formData = new FormData();
    formData.append('type', documentType);
    formData.append('file', file);

    const response = await api.post<{
      success: boolean;
      error?: string;
    }>(`${BANKING_BASE}/kyc/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    if (!response.data.success) {
      throw new Error(response.data.error || 'Falha ao enviar documento');
    }
  }

  async getAccountLimits(): Promise<AccountLimits> {
    const response = await api.get<{
      success: boolean;
      data?: AccountLimits;
      error?: string;
    }>(`${BANKING_BASE}/kyc/limits`);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Falha ao carregar limites');
  }

  async getSecurityAlerts(): Promise<SecurityAlert[]> {
    const response = await api.get<{
      success: boolean;
      data?: SecurityAlert[];
      error?: string;
    }>(`${BANKING_BASE}/kyc/alerts`);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Falha ao carregar alertas');
  }

  async acknowledgeAlert(alertId: string): Promise<void> {
    const response = await api.post<{
      success: boolean;
      error?: string;
    }>(`${BANKING_BASE}/kyc/alerts/${alertId}/acknowledge`);

    if (!response.data.success) {
      throw new Error(response.data.error || 'Falha ao confirmar alerta');
    }
  }

  // ============================================
  // PAGAMENTO DE FATURAS/SERVIÇOS
  // ============================================

  async getProviders(category?: string): Promise<ServiceProvider[]> {
    const url = category
      ? `${BANKING_BASE}/bills/providers?category=${category}`
      : `${BANKING_BASE}/bills/providers`;

    const response = await api.get<{
      success: boolean;
      data?: ServiceProvider[];
      error?: string;
    }>(url);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Falha ao carregar provedores');
  }

  async getProviderDetails(providerId: number): Promise<ServiceProvider> {
    const response = await api.get<{
      success: boolean;
      data?: ServiceProvider;
      error?: string;
    }>(`${BANKING_BASE}/bills/providers/${providerId}`);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Falha ao carregar provedor');
  }

  async validateBill(providerId: number, reference: string): Promise<BillDetails> {
    const response = await api.post<{
      success: boolean;
      data?: BillDetails;
      error?: string;
    }>(`${BANKING_BASE}/bills/validate`, { providerId, reference });

    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Falha ao validar fatura');
  }

  async payBill(request: BillPaymentRequest): Promise<BillPaymentResult> {
    const response = await api.post<{
      success: boolean;
      data?: BillPaymentResult;
      error?: string;
    }>(`${BANKING_BASE}/bills/pay`, request);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Falha no pagamento');
  }

  async getBillHistory(filters?: BankingFilters): Promise<BillPaymentResult[]> {
    const params = new URLSearchParams();
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);

    const query = params.toString();
    const url = `${BANKING_BASE}/bills/history${query ? `?${query}` : ''}`;

    const response = await api.get<{
      success: boolean;
      data?: BillPaymentResult[];
      error?: string;
    }>(url);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Falha ao carregar histórico');
  }

  async getBillFavorites(): Promise<BillFavorite[]> {
    const response = await api.get<{
      success: boolean;
      data?: BillFavorite[];
      error?: string;
    }>(`${BANKING_BASE}/bills/favorites`);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Falha ao carregar favoritos');
  }

  async addBillFavorite(providerId: number, reference: string, alias: string): Promise<BillFavorite> {
    const response = await api.post<{
      success: boolean;
      data?: BillFavorite;
      error?: string;
    }>(`${BANKING_BASE}/bills/favorites`, { providerId, reference, alias });

    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Falha ao adicionar favorito');
  }

  async removeBillFavorite(favoriteId: string): Promise<void> {
    const response = await api.delete<{
      success: boolean;
      error?: string;
    }>(`${BANKING_BASE}/bills/favorites/${favoriteId}`);

    if (!response.data.success) {
      throw new Error(response.data.error || 'Falha ao remover favorito');
    }
  }

  async getRecurringBills(): Promise<RecurringBill[]> {
    const response = await api.get<{
      success: boolean;
      data?: RecurringBill[];
      error?: string;
    }>(`${BANKING_BASE}/bills/recurring`);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Falha ao carregar recorrentes');
  }

  async createRecurringBill(favoriteId: string, frequency: string, autoPayEnabled: boolean): Promise<RecurringBill> {
    const response = await api.post<{
      success: boolean;
      data?: RecurringBill;
      error?: string;
    }>(`${BANKING_BASE}/bills/recurring`, { favoriteId, frequency, autoPayEnabled });

    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Falha ao criar recorrente');
  }

  async getBillAnalytics(period?: string): Promise<BillAnalytics> {
    const url = period
      ? `${BANKING_BASE}/bills/analytics?period=${period}`
      : `${BANKING_BASE}/bills/analytics`;

    const response = await api.get<{
      success: boolean;
      data?: BillAnalytics;
      error?: string;
    }>(url);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Falha ao carregar analytics');
  }
}

export default new BankingService();
